/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { UserProfile } from './user-profile';
import { getPath } from '../../utils';


async function getActiveSessionCount({ sketchesHost, enableSessionLimit }) {
  if (!enableSessionLimit) return;
  const url = getPath({ sketchesHost, relativePath: '/api/auth/v1/sessions' })
  const { count } = await (await fetch(url)).json();
  return count
}

function removeSessionFlag() {
  window.localStorage.removeItem("activeSession");
}

function addSessionFlag() {
  window.localStorage.setItem("activeSession", true)
}

function navigateToSessionExeededPage({ sketchesHost, whiteListedUrlParams }) {
  const isSessionExcededPage = window.location.pathname.includes('/session-exceeded');
  if (!isSessionExcededPage) {
    global?.localStorage?.setItem('sessionExceededRedirectUrl', window?.location?.href);
    window.location.href = getPath({
      sketchesHost,
      relativePath: '/session-exceeded',
      injectCustomParams: true,
      whiteListedUrlParams
    });
  }
}

function checkLimitExceeded({ enableSessionLimit, activeSessionCount, maxSessionsPerUser }) {
  const isActiveSession = window.localStorage.getItem("activeSession");
  return enableSessionLimit && activeSessionCount > parseInt(maxSessionsPerUser) && !isActiveSession
}

const HeaderLoginBase = props => {
  const {
    headerTheme,
    enableSSO,
    localizedElementsData = {},
    member,
    memberLoaded,
    sso = {},
    sketchesHost,
    whiteListedUrlParams = [],
    maxSessionsPerUser,
    enableSessionLimit,
  } = props;
  const { signInSignUp = {} } = localizedElementsData;
  const { signIn: localizedSignIn } = signInSignUp;
  const headerType = headerTheme.includes('single-layer') ? 'single-layer' : '';

  useEffect(() => {
    if (enableSSO && !member) updateMember();
  }, [member]);

  const updateMember = async () => {
    const { currentUser } = await import('@quintype/bridgekeeper-js');
    try {
      const [data, activeSessionCount] = await Promise.all([currentUser(), getActiveSessionCount({ sketchesHost, enableSessionLimit })]);
      const { user, error } = data || {};
      if (!user) enableSessionLimit && removeSessionFlag();
      const sessionLimitExceeded = checkLimitExceeded({ enableSessionLimit, activeSessionCount, maxSessionsPerUser })
      if (sessionLimitExceeded) navigateToSessionExeededPage({ sketchesHost, whiteListedUrlParams });
      else {
        if (user || error) memberLoaded(true);
        if (user?.['verification-status']) {
          props.updateMember(user);
          enableSessionLimit && addSessionFlag()
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  if (!enableSSO) return null;
  return (
    <UserProfile
      member={member}
      customCssClasses={headerType}
      localizedSignIn={localizedSignIn}
      sso={sso}
      sketchesHost={sketchesHost}
      whiteListedUrlParams={whiteListedUrlParams}
    />
  );
};

HeaderLoginBase.propTypes = {
  headerTheme: PropTypes.string,
  sketchesHost: PropTypes.string,
  enableSSO: PropTypes.bool,
  localizedElementsData: PropTypes.shape({
    buttonLabels: PropTypes.shape({
      signIn: PropTypes.string
    })
  }),
  updateMember: PropTypes.func,
  member: PropTypes.object,
  sso: PropTypes.object
};

const mapStateToProps = state => {
  const { enableLocalization = false, localizedElements = {} } = get(
    state.qt,
    ['config', 'pagebuilder-config', 'general', 'localization'],
    {}
  );
  return {
    headerTheme: get(state.qt, ['config', 'pagebuilder-config', 'header', 'headerStyle'], 'header_1'),
    enableSSO: get(state.qt, ['config', 'pagebuilder-config', 'header', 'enableSSO'], false),
    localizedElementsData: enableLocalization ? localizedElements : {},
    sso: get(state.qt, ['config', 'pagebuilder-config', 'general', 'sso'], {}),
    sketchesHost: get(state.qt, ['config', 'sketches-host']),
    member: get(state, ['member'], null),
    whiteListedUrlParams: get(
      state.qt,
      ['config', 'pagebuilder-config', 'general', 'others', 'whiteListedUrlParams'],
      []
    ),
    enableSessionLimit: get(state.qt, ['config', 'pagebuilder-config', 'sessionLimit', 'enableSessionLimit'], false),
    maxSessionsPerUser: get(state.qt, ['config', 'pagebuilder-config', 'sessionLimit', 'maxSessionsPerUser'], 0),
  };
};

const mapDispatchToProps = dispatch => ({
  updateMember: member => dispatch({ type: 'MEMBER_UPDATED', member }),
  memberLoaded: memberLoaded => dispatch({ type: 'MEMBER_LOADED', memberLoaded })
});

export const HeaderLogin = connect(mapStateToProps, mapDispatchToProps)(HeaderLoginBase);

export { HeaderLoginBase };
